import { Grid, Heading, Icon, Text, TextSizes, useThemePerClass, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { ErrorCode } from '@afterpaytouch/portal-api'
import React, { FunctionComponent, ReactNode } from 'react'
import { PayableActiveOrders, Money as MoneyComponent, Money } from '../'
import { CheckoutError } from '../../model'
import { useCheckoutState, useIsRecoverableFlowPageReady } from '../../state'
import styles from './style.module.css'
import { isNullOrUndefined } from '@afterpay/utils'
import { useTranslation } from '../../utils/convergence'
import { selectRecoverableTerminalErrorI18nConfig } from './recoverableTerminalErrorI18n'

export const RecoverableTerminalError: FunctionComponent<CheckoutError> = ({ error, errorCode, extra }) => {
  const { t } = useTranslation()
  const { checkout } = useCheckoutState()
  const isPageReady = useIsRecoverableFlowPageReady()
  const isArcadeDesignLangauge = useIsDesignLanguage(DesignLanguage.Arcade)
  const themedStyles = useThemePerClass(styles)
  const translationDataMap = selectRecoverableTerminalErrorI18nConfig(error)

  const headingSize: TextSizes = isArcadeDesignLangauge ? 'XL' : 'XXL'
  const description = translationDataMap.description(t)

  const renderIcon = (): ReactNode => {
    if (!isArcadeDesignLangauge) {
      return null
    }
    return (
      <div className={styles.headingIconWrapper} data-testid='recoverable-terminal-error-heading-icon'>
        <Icon name='AlertFill' color='Fire' size='Large' />
      </div>
    )
  }

  const renderLimits = (): ReactNode => {
    if (isNullOrUndefined(extra?.availableToSpend)) {
      return null
    }

    if (isArcadeDesignLangauge) {
      return (
        <div className={themedStyles.limitsWrapper} data-testid='recoverable-terminal-error-limits'>
          <Grid verticalGutter={{ sm: 1, md: 1, lg: 1 }}>
            <Grid.Item sm={12}>
              <div className={styles.limitsRowItem}>
                <Text>{t('recoverableTerminalError:limits.orderTotal')}</Text>
                <Money size={'M'} value={checkout?.amount} />
              </div>
            </Grid.Item>
            <Grid.Item sm={12}>
              <div className={styles.limitsRowItem}>
                <Text>{t('recoverableTerminalError:limits.availableToSpend')}</Text>
                <Money size={'M'} value={extra?.availableToSpend} />
              </div>
            </Grid.Item>
          </Grid>
        </div>
      )
    }

    return (
      <div className={styles.limitsWrapper} data-testid='terminal-error'>
        <Money mintBar value={extra?.availableToSpend} size='XXXL' bold />
        <Text bold>{t('recoverableTerminalError:availableToSpend')}</Text>
      </div>
    )
  }

  const renderDescription = (): JSX.Element => {
    if (isArcadeDesignLangauge) {
      return (
        <Text renderAs='div' testNameSpace={errorCode}>
          {description}
        </Text>
      )
    }

    return (
      <Text renderAs='div' testNameSpace={errorCode}>
        {description.length > 0 &&
          translationDataMap.renderDescription({
            values: extra,
            cartTotal: <MoneyComponent size='M' bold value={checkout?.amount} />,
          })}
      </Text>
    )
  }

  return (
    <>
      <Grid>
        <div>
          {isPageReady && (
            <>
              {renderIcon()}
              <Heading size={headingSize} testNameSpace={errorCode}>
                {translationDataMap.heading(t)}
              </Heading>
              {renderLimits()}
              <div className={themedStyles.recoverableDescriptionWrapper}>{renderDescription()}</div>
            </>
          )}
          <PayableActiveOrders filterBy={errorCode as ErrorCode} />
        </div>
      </Grid>
    </>
  )
}
